import React, { useState } from "react";
import Button from "../../../shared/ui/Button";
import Input from "../../../shared/ui/Input";
import {
  useTranslation,
  mapRegisterErrorsI18n,
  validateRegisterStepOneI18n,
  validateRegisterStepTwoI18n,
} from "../../../i18n";
import {
  INITIAL_REGISTER_FORM,
  buildRegisterPayload,
} from "../../../auth/registerUtils";
import { registerAdminUser } from "../adminApi";

const AdminRegisterUserModal = ({ open, onClose, onRegistered }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_REGISTER_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  if (!open) return null;

  const resetForm = () => {
    setStep(1);
    setFormData(INITIAL_REGISTER_FORM);
    setErrors({});
    setSuccessMessage("");
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    const stepErrors = validateRegisterStepOneI18n(formData, t);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stepErrors = validateRegisterStepTwoI18n(formData, t);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;

    setIsSubmitting(true);
    setErrors({});
    try {
      const created = await registerAdminUser(buildRegisterPayload(formData));
      setSuccessMessage(
        t("signup.userRegistered", {
          name: created.fullName,
          email: created.email,
        })
      );
      onRegistered?.(created);
      window.setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (err) {
      setErrors(mapRegisterErrorsI18n(err?.data, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-register-title"
      onClick={handleClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[16px] border border-[#e8ecf0] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2
              id="admin-register-title"
              className="text-[20px] font-bold text-[#0f172a]"
            >
              {t("signup.adminRegisterTitle")}
            </h2>
            <p className="mt-1 text-[13px] text-[#64748b]">
              {t("signup.adminRegisterSubtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg px-2 py-1 text-[#64748b] hover:bg-[#f1f5f9]"
            aria-label={t("common.close")}
          >
            ✕
          </button>
        </header>

        {successMessage ? (
          <p className="rounded-[10px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">
            {successMessage}
          </p>
        ) : null}

        {step === 1 && !successMessage && (
          <form onSubmit={handleNext} className="space-y-4">
            <Input
              type="text"
              name="fullName"
              label={t("signup.fullNameLabel")}
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Max Mustermann"
              error={errors.fullName}
            />
            <Input
              type="email"
              name="email"
              label={t("auth.email")}
              value={formData.email}
              onChange={handleChange}
              placeholder="max@unternehmen.de"
              error={errors.email}
            />
            <Input
              type="password"
              name="password"
              label={t("auth.password")}
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••••••"
              hint={t("auth.passwordMinLength")}
              error={errors.password}
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={handleClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("auth.next")}</Button>
            </div>
          </form>
        )}

        {step === 2 && !successMessage && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                type="text"
                name="companyName"
                label={t("auth.companyName")}
                value={formData.companyName}
                onChange={handleChange}
                error={errors.companyName}
              />
              <Input
                type="text"
                name="industry"
                label={t("auth.industry")}
                value={formData.industry}
                onChange={handleChange}
                error={errors.industry}
              />
              <Input
                type="email"
                name="businessEmail"
                label={t("auth.businessEmail")}
                value={formData.businessEmail}
                onChange={handleChange}
                error={errors.businessEmail}
              />
              <Input
                type="text"
                name="businessAddress"
                label={t("auth.businessAddress")}
                value={formData.businessAddress}
                onChange={handleChange}
                error={errors.businessAddress}
              />
            </div>
            {errors.form ? (
              <p className="text-[13px] text-red-600" role="alert">
                {errors.form}
              </p>
            ) : null}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(1)}
                disabled={isSubmitting}
              >
                {t("common.back")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("signup.registering")
                  : t("signup.completeRegistration")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminRegisterUserModal;
